import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Card, Button, Switch, Group, Box } from "@mantine/core";
import { useParams, Route, Routes } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  ErrorView,
  AvatarManagement,
  PageTitle,
  Subnav,
} from "@components/shared";
import { AdminForm, AdminRolesPermissions } from "@components/Admin";
import { PasswordForm, PasskeyManager } from "@components/Auth";

import useAppStore from "@duck/app-store";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

export default function AdminProfile({ isProfile }) {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [admin, setAdmin] = useState(populateAdmin());
  const params = useParams();
  const { id } = params;
  const profileAdminId = useAppStore((state) => state.admin)?.id;
  const adminId = isProfile ? profileAdminId : id;

  useEffect(() => {
    fetchData();
  }, []);

  function fetchData() {
    axios
      .get(`/admins/${adminId}/`)
      .then(({ data }) => {
        setAdmin(populateAdmin(data.response[0]));
      })
      .catch((err) => {
        setError(err);
        setAdmin(null);
      });
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!admin) return null;

  return (
    <div>
      <Group mb="lg">
        <AvatarManagement asset={admin.profile_asset} adminId={adminId} />
        <PageTitle
          title={`${admin.first_name} ${admin.last_name}`}
          subtitle={"Admin"}
          icon={entityIcon.admin()}
          iconColor={entityColor.admin}
        />
      </Group>
      {!isProfile && (
        <Box mt="md" mb="md">
          <Subnav
            links={[
              { text: t("detail"), to: `/admins/${adminId}` },
              {
                text: t("role_plural"),
                to: `/admins/${adminId}/roles`,
              },
            ]}
          />
        </Box>
      )}
      {isProfile ? (
        <>
          <Card>
            <AdminForm
              id={adminId}
              firstName={admin.first_name}
              lastName={admin.last_name}
              email={admin.email}
              mfa={admin.mfa}
              onSuccess={fetchData}
            />
          </Card>
          <ChangePassword adminId={adminId} />
          <PasskeyManager />
        </>
      ) : (
        <Routes>
          <Route
            path="/"
            element={
              <>
                <Card>
                  <AdminForm
                    id={adminId}
                    firstName={admin.first_name}
                    lastName={admin.last_name}
                    email={admin.email}
                    mfa={admin.mfa}
                    onSuccess={fetchData}
                  />
                </Card>
                <Card mt="sm">
                  <AdminStatus
                    adminId={adminId}
                    fetchData={fetchData}
                    status={admin.status}
                    isSelf={String(adminId) === String(profileAdminId)}
                  />
                </Card>
              </>
            }
          />
          <Route
            path="/roles"
            element={<AdminRolesPermissions adminId={adminId} />}
          />
        </Routes>
      )}
    </div>
  );
}

function populateAdmin(d = {}) {
  return {
    first_name: d.first_name || "",
    email: d.email || "",
    last_name: d.last_name || "",
    mfa: d.mfa || false,
    profile_asset: d.profile_asset || null,
    status: d.status || null,
  };
}

function ChangePassword({ adminId }) {
  const [isOpen, setOpen] = useState(false);

  return (
    <React.Fragment>
      <Button
        variant="subtle"
        color="gray"
        mt="lg"
        size="xs"
        onClick={() => setOpen(true)}
      >
        Change Password
      </Button>
      <Modal
        opened={isOpen}
        onClose={() => setOpen(false)}
        title="Change Password"
      >
        <PasswordForm
          onSuccess={() => setOpen(false)}
          updating
          additionalReqData={{
            admin_id: adminId,
          }}
        />
      </Modal>
    </React.Fragment>
  );
}

function AdminStatus({ adminId, fetchData, status, isSelf = false }) {
  function changeStatus() {
    if (isSelf && status === 2) return;

    const req = {
      admin_id: adminId,
    };

    axios
      .post(
        `/admins/${adminId}/${status === 2 ? "inactivate" : "activate"}/`,
        req
      )
      .then(() => {
        fetchData();
      })
      .catch((err) => {
        toast.error(err);
        fetchData();
      });
  }

  return (
    <Switch
      description={
        isSelf
          ? "You cannot deactivate your own admin account."
          : status === 2
            ? "Unchecking will deactivate this admin preventing them from logging in."
            : "Checking will reactivate this admin allowing them to log in."
      }
      label="Activation Status"
      disabled={isSelf}
      onClick={() => changeStatus()}
      checked={status === 2}
    />
  );
}
